/*
 * @Author: Mr.mjc
 * @Date: 2022-08-17 16:43:47
 * @LastEditors: MJC
 * @LastEditTime: 2023-03-15 18:11:18
 * @Description: 区分表格模式出口文件
 * @FilePath: /next-ui/packages/wui-table/src/IsSticky.tsx
 */
import React, { useMemo, Fragment, useCallback } from 'react';
import omit from 'omit.js';
import Table from './Table';
import StickyTable from './stickyTable/StickyTable';
import { TableProps, DefaultRecordType } from './iTable';
import TablePage from './lib/tablePage';
import { sticky } from './lib/util';
import { prefix } from "../../wui-core/src/index";
import {getLangInfo} from "../../wui-locale/src/tool";
import i18n from "./lib/i18n";
import usePagination, { getPaginationParam } from './hooks/usePagination';
import useSelect from './hooks/useSelect'


const renderTable: React.FC<Partial<TableProps>> = React.forwardRef((props: TableProps, ref) => {
    // 大数据模式下会传过来截取完的data,拿不到完整的data,影响多选单选的selectKeys全选取值会少
    let {
        scrollMode,
        pagination = false,
        data: innerData = [], // 大数据下分割后的数据
        originData, // 大数据下全部数据
        showSum,
        sumdata,
        antd,
        onChange: onOutChange,
        fieldid,
        rowKey = 'key',
        columns,
        multiSelectConfig,
        children,
        childrenColumnName = 'children',
        getSelectedDataFunc,
        autoSelect,
        rowSelection,
        selectType,
        onRowClick,
        autoCheckedByClickRows = true,
        expandIconColumnIndex,
        selectedRowIndex,
        expandable, // 兼容新版的antd的API，此层处理兼容
        expandIconCellWidth,
        defaultExpandAllRows,
        defaultExpandedRowKeys,
        expandedRowClassName,
        expandedRowKeys,
        expandedRowRender,
        expandIcon,
        expandRowByClick,
        indentSize,
        onExpand,
        onExpandedRowsChange,
        expandIconAsCell,
        haveExpandIcon,
        showExpandIcon,
        expandedIcon,
        collapsedIcon,
        showHeaderExpandIcon,
        onExpandAll,
        locale
    } = props;

    const { checkStrictly = true } = rowSelection || {};

    const getRowKey = useMemo(() => {
        return typeof rowKey === 'function' ? rowKey : (record: DefaultRecordType) => (record[rowKey as string])
    }, [rowKey])

    const expandableProps = useMemo(() => {
        return {
            childrenColumnName: expandable?.childrenColumnName || childrenColumnName,
            expandableColumnTitle: expandable?.columnTitle,
            expandIconCellWidth: expandable?.columnWidth || expandIconCellWidth,
            defaultExpandAllRows: expandable?.defaultExpandAllRows || defaultExpandAllRows,
            defaultExpandedRowKeys: expandable?.defaultExpandedRowKeys || defaultExpandedRowKeys,
            expandedRowClassName: expandable?.expandedRowClassName || expandedRowClassName,
            expandedRowKeys: expandable?.expandedRowKeys || expandedRowKeys,
            expandedRowRender: expandable?.expandedRowRender || expandedRowRender,
            expandIcon: expandable?.expandIcon || expandIcon,
            expandRowByClick: expandable?.expandRowByClick || expandRowByClick,
            expandIconFixed: expandable?.fixed,
            indentSize: expandable?.indentSize || indentSize,
            showExpandColumn: expandable?.showExpandColumn,
            onExpand: expandable?.onExpand || onExpand,
            onExpandedRowsChange: expandable?.onExpandedRowsChange || onExpandedRowsChange,
            expandIconAsCell: expandable?.expandIconAsCell || expandIconAsCell,
            expandIconColumnIndex: expandable?.expandIconColumnIndex || expandIconColumnIndex,
            haveExpandIcon: expandable?.haveExpandIcon || haveExpandIcon,
            showExpandIcon: expandable?.showExpandIcon || showExpandIcon,
            expandedIcon: expandable?.expandedIcon || expandedIcon,
            collapsedIcon: expandable?.collapsedIcon || collapsedIcon,
            showHeaderExpandIcon: expandable?.showHeaderExpandIcon || showHeaderExpandIcon,
            onExpandAll: expandable?.onExpandAll || onExpandAll
        }
    },
    [
        expandable,
        childrenColumnName,
        expandIconCellWidth,
        defaultExpandAllRows,
        defaultExpandedRowKeys,
        expandedRowClassName,
        expandedRowKeys,
        expandedRowRender,
        expandIcon,
        expandRowByClick,
        indentSize,
        onExpand,
        onExpandedRowsChange,
        expandIconAsCell,
        expandIconColumnIndex,
        haveExpandIcon,
        showExpandIcon,
        expandedIcon,
        collapsedIcon,
        showHeaderExpandIcon,
        onExpandAll
    ])

    const changeEventInfo:any = {} // 缓存后续一些改变的值sort,filter,pagination

    // 内部一些如sort排序无改变引用地址，故监听不到，需处理
    let data = useMemo(() => {
        return [
            ...innerData
        ]
    }, [innerData, [...innerData]])

    const dataLength = useMemo(() => {
        return data.length
    }, [data])

    const triggerOnChange = (
        info: any,
        action: string,
        reset: boolean = false
    ) => {

        const changeInfo = {
            ...changeEventInfo,
            ...info
        }
        if (reset) {
            changeEventInfo.resetPagination!();
        }

        onOutChange?.(changeInfo.pagination!, {}, {}, {
            currentDataSource: data,
            action,
        });
    }

    const onPaginationChange = (current: number, pageSize: number) => {
        triggerOnChange(
            {
                pagination: { ...changeEventInfo.pagination, current, pageSize },
            },
            'paginate',
        );
    };

    const [mergedPagination, resetPagination] = usePagination(
        data.length,
        pagination,
        onPaginationChange,
    );

    changeEventInfo.pagination = pagination === false ? {} : getPaginationParam(pagination, mergedPagination);

    changeEventInfo.resetPagination = resetPagination;
    let _scrollMode = useMemo(() => {
        if (scrollMode) {
            return scrollMode;
        } else {
            let hasSticky = sticky();
            if (!hasSticky) { // 不支持
                return 'table'
            } else {
                return 'sticky'
            }
        }
    }, [scrollMode])

    // 展示的数据，涉及前端分页
    const showData = useMemo(() => {
        if (antd) { // table.AntdTable
            return data
        } else {
            if (pagination === false || !mergedPagination.pageSize) {
                return data
            }
            const {current = 1, pageSize = 10, total} = mergedPagination || {};
            if (data.length < total!) {
                if (data.length > pageSize) {
                    return (data || []).slice((current - 1) * pageSize, current * pageSize);
                }
                return data
            }
            return (data || []).slice((current - 1) * pageSize, current * pageSize);
        }
    }, [
        !!pagination,
        data,
        mergedPagination && mergedPagination.current,
        mergedPagination && mergedPagination.pageSize,
        mergedPagination && mergedPagination.total,
        antd
    ])


    const [innerColumns, selectedRowKeys, setByClickRowsChange] = useSelect(rowSelection, {
        columns,
        data: originData || innerData,
        showData: originData || showData, // 大数据 + 全选性能
        selectType,
        getRowKey,
        multiSelectConfig,
        children,
        childrenColumnName,
        getSelectedDataFunc,
        checkStrictly,
        autoSelect,
        autoCheckedByClickRows,
        selectedRowIndex,
        antd
    })

    const onInnerRowClick = useCallback((record: DefaultRecordType, index: number, event: React.MouseEvent<HTMLElement>) => {
        if (autoCheckedByClickRows && (!!selectType || rowSelection !== undefined)) { // 多选单选
            setByClickRowsChange(record, index, event)
        }
        onRowClick && onRowClick(record, index, event);
    }, [originData, innerData, autoCheckedByClickRows, selectType, rowSelection, selectedRowKeys])

    let hasPageTop = useMemo(() => {
        if (pagination === false || mergedPagination === false) {
            return null
        } else {
            let {
                position = ['bottomRight'],
                ref
            } = mergedPagination;
            if (Object.prototype.toString.call(mergedPagination) === '[object Object]') {
                let pageProps = Object.assign({}, {...mergedPagination}, { total: dataLength })
                let topPos = position.find((item: string) => item.includes('top'))
                if (topPos) {
                    let currentTop = topPos.toLowerCase().replace('top', '')
                    let fieldidAttr = fieldid ? { fieldid } : {}
                    let refAttr = ref ? { ref } : {}
                    return <TablePage {...omit(pageProps, ['position'])} className={`${prefix}-table-page ${prefix}-table-page-${currentTop}`} {...fieldidAttr} {...refAttr}/>
                }
                return null
            }
            return null
        }
    }, [mergedPagination, pagination, dataLength])

    let hasPageBottom = useMemo(() => {
        if (pagination === false || mergedPagination === false) {
            return null
        } else {
            let {
                position = ['bottomRight'],
                ref
            } = mergedPagination;
            if (Object.prototype.toString.call(mergedPagination) === '[object Object]') {
                let pageProps = Object.assign({}, {...mergedPagination}, { total: dataLength })
                let bottomPos = position.find((item: string) => item.includes('bottom'))
                if (bottomPos) {
                    let currentBottom = bottomPos.toLowerCase().replace('bottom', '')
                    let fieldidAttr = fieldid ? { fieldid } : {}
                    let refAttr = ref ? { ref } : {}
                    return <TablePage {...omit(pageProps, ['position'])} className={`${prefix}-table-page ${prefix}-table-page-${currentBottom}`} {...fieldidAttr} {...refAttr}/>
                }
                return null
            }
            return null
        }
    }, [mergedPagination, pagination, dataLength])

    // 合计及后续添加column后的处理
    const realData = useMemo(() => {
        if (showSum && sumdata && showData.length) {
            let realColumns = innerColumns(columns);
            let firstColumn = realColumns[0];
            if (firstColumn.dataIndex) {
                sumdata[firstColumn.dataIndex] = '';
                let _locale = getLangInfo(locale, i18n)
                let totalStr = _locale.langMap.total || '合计'
                sumdata[firstColumn.dataIndex] = `${totalStr} ` + (sumdata[firstColumn.dataIndex] || '');
            }
            return showData.concat([sumdata])
        } else {
            return showData;
        }

    }, [showData, showSum, sumdata, columns, locale])

    let renderContent = useMemo(() => {
        let renderProps = {
            ...props,
            ...expandableProps,
            data: realData,
            columns: innerColumns(columns),
            selectedRowKeys,
            onRowClick: onInnerRowClick,
            expandIconColumnIndex: (!!selectType || rowSelection !== undefined) && expandIconColumnIndex ? expandIconColumnIndex + 1 :
                (!!selectType || rowSelection !== undefined) && !expandIconColumnIndex ? 1 : expandIconColumnIndex ? expandIconColumnIndex : 0
        }
        let tableContent = _scrollMode === 'sticky' ?
            // @ts-ignore
            <StickyTable {...renderProps} ref={ref}></StickyTable> :
            // @ts-ignore
            <Table {...renderProps} ref={ref}></Table>;
        let showContent = <Fragment>
            { !antd && hasPageTop }
            { tableContent }
            { !antd && hasPageBottom }
        </Fragment>
        return showContent;
    },
    [_scrollMode, hasPageTop, hasPageBottom, antd, showData]
    )

    return renderContent;
})
export default renderTable;